"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.checkPronunciationWords = checkPronunciationWords;
function normalize(word) {
    return word
        .normalize("NFKC")
        .toLowerCase()
        .replace(/[’‘]/gu, "'")
        .replace(/[^\p{L}\p{N}']/gu, "");
}
function substitutionCost(left, right) {
    const a = Array.from(normalize(left)), b = Array.from(normalize(right));
    if (a.join("") === b.join(""))
        return 0;
    let previous = Array.from({ length: b.length + 1 }, (_, index) => index);
    for (let row = 1; row <= a.length; row++) {
        const next = [row];
        for (let column = 1; column <= b.length; column++)
            next[column] = Math.min(previous[column] + 1, next[column - 1] + 1, previous[column - 1] + (a[row - 1] === b[column - 1] ? 0 : 1));
        previous = next;
    }
    return 1 + previous[b.length] / Math.max(a.length, b.length, 1);
}
function tokenize(text) {
    return (text.match(/[\p{Script=Han}\p{Script=Hiragana}\p{Script=Katakana}ー]|[\p{L}\p{N}]+(?:['’‘][\p{L}\p{N}]+)*/gu) ?? []);
}
function align(referenceWords, transcript) {
    const tokenGroups = referenceWords.map(tokenize);
    const reference = tokenGroups.flat();
    const heard = tokenize(transcript);
    const matrix = Array.from({ length: reference.length + 1 }, () => new Array(heard.length + 1).fill(0));
    for (let row = 0; row <= reference.length; row++)
        matrix[row][0] = row;
    for (let column = 0; column <= heard.length; column++)
        matrix[0][column] = column;
    for (let row = 1; row <= reference.length; row++)
        for (let column = 1; column <= heard.length; column++) {
            const cost = substitutionCost(reference[row - 1], heard[column - 1]);
            matrix[row][column] = Math.min(matrix[row - 1][column - 1] + cost, matrix[row - 1][column] + 1, matrix[row][column - 1] + 1);
        }
    const words = new Array(reference.length);
    const inserted = [];
    let row = reference.length, column = heard.length;
    while (row || column) {
        const equal = row > 0 &&
            column > 0 &&
            normalize(reference[row - 1]) === normalize(heard[column - 1]);
        if (row &&
            column &&
            matrix[row][column] ===
                matrix[row - 1][column - 1] +
                    substitutionCost(reference[row - 1], heard[column - 1])) {
            words[row - 1] = {
                status: equal ? "matched" : "different",
                recognizedWord: heard[column - 1],
            };
            row--;
            column--;
        }
        else if (row && matrix[row][column] === matrix[row - 1][column] + 1) {
            words[row - 1] = { status: "missing", recognizedWord: null };
            row--;
        }
        else {
            inserted.unshift(heard[column - 1]);
            column--;
        }
    }
    let cursor = 0;
    const grouped = tokenGroups.map((tokens) => {
        const entries = words.slice(cursor, cursor + tokens.length);
        cursor += tokens.length;
        return {
            status: entries.some((entry) => entry.status === "different")
                ? "different"
                : entries.some((entry) => entry.status === "missing")
                    ? "missing"
                    : "matched",
            recognizedWord: entries.map((entry) => entry.recognizedWord ?? "").join("") ||
                null,
        };
    });
    return {
        words: grouped,
        inserted,
        distance: matrix[reference.length][heard.length],
    };
}
/** Speech recognition checks the spoken words; provider pronunciation scores remain unchanged. */
function checkPronunciationWords(words, candidates, minConfidence, alternativeMargin, referenceText) {
    const reference = words.filter((word) => word.errorType !== "Insertion");
    const primary = [...candidates].sort((left, right) => right.confidence - left.confidence)[0] ?? { text: "", confidence: 0 };
    const referenceAligned = referenceText === undefined ||
        normalize(reference.map((word) => word.word).join("")) ===
            normalize(referenceText);
    const best = align(reference.map((word) => word.word), primary.text);
    const alternatives = candidates
        .filter((candidate) => candidate !== primary &&
        primary.confidence - candidate.confidence <= alternativeMargin)
        .map((candidate) => align(reference.map((word) => word.word), candidate.text));
    let cursor = 0;
    const output = words.map((word) => {
        if (word.errorType === "Insertion")
            return { ...word };
        const index = cursor++, verification = best.words[index];
        const disagrees = alternatives.some((alternative) => alternative.words[index].status !== verification.status ||
            normalize(alternative.words[index].recognizedWord ?? "") !==
                normalize(verification.recognizedWord ?? ""));
        return {
            ...word,
            verification: !referenceAligned || primary.confidence < minConfidence || disagrees
                ? { status: "uncertain", recognizedWord: verification.recognizedWord }
                : verification,
        };
    });
    const uncertain = output.some((word) => word.verification?.status === "uncertain");
    return {
        words: output,
        wordCheck: {
            status: uncertain ? "uncertain" : "verified",
            transcript: primary.text,
            confidence: primary.confidence,
            matchedWords: output.filter((word) => word.verification?.status === "matched").length,
            referenceWords: reference.length,
            insertedWords: best.inserted,
        },
    };
}
//# sourceMappingURL=pronunciationWordCheck.js.map